import styled, { useTheme } from 'styled-components'
import { useCallback, useEffect, useRef, useState } from 'react'
import Icon from '@/components/icon/Icon'
import { InfoCircle, QuestionCircle, Sizes } from '@/components/icon/IconList'
import useWindowDimensions from '@/lib/web-ui/useWindowDimensions'

interface TooltipWithIconProps {
	icon: 'question' | 'info'
	message: string | React.ReactNode
	color: string
	size?: Sizes
	children?: string | React.ReactNode
	link?: string
	isHover?: boolean
}

export const TooltipWithIcon = ({
	icon,
	message,
	color,
	size,
	children,
	link,
	isHover,
}: TooltipWithIconProps) => {
	const theme = useTheme()
	const isMobile = useWindowDimensions().width < 1200
	const renderIcon = useCallback(() => {
		switch (icon) {
			case 'question':
				return <Icon icon={QuestionCircle} size={size} color={color} />
			case 'info':
				return <Icon icon={InfoCircle} size={size} color={color} />
			default:
				return null
		}
	}, [icon])
	const tooltipRef = useRef(null)
	const [tooltipTop, setTooltipTop] = useState(150)
	const [isHovered, setIsHovered] = useState(false)
	useEffect(() => {
		if (tooltipRef.current) {
			const tooltipHeight = tooltipRef.current.clientHeight + 16
			setTooltipTop(tooltipHeight)
		}
	}, [message, isHovered])

	return (
		<Tooltip>
			<div
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				className={'tooltip'}
				style={{ color: color, top: -tooltipTop }}
				ref={tooltipRef}>
				<div className={'tooltip-message'}>{message}</div>
			</div>
			<>{children}</>
			{!!icon && <span>{renderIcon()}</span>}
		</Tooltip>
	)
}

const Tooltip = styled.div`
	max-width: 410px;

	&:hover {
		.tooltip {
			display: flex;
			opacity: 1;
			visibility: visible;
			transform: scale(1);
			@media (max-width: 1200px) {
				display: none;
			}
		}
	}

	.tooltip {
		z-index: 1;
		transition: all 0.3s ease;
		transform: scale(0.8);
		opacity: 0;
		max-width: 410px;
		position: absolute;
		box-shadow: 6px 6px 20px 0px rgba(28, 31, 46, 0.12);
		visibility: hidden;
		top: -150px;
		left: 0;
		white-space: nowrap;
		padding: 20px;
		border-radius: 4px;
		background: #ffff;

		> div {
			display: flex;
			font-size: 14px;
			color: #4f556b;
			width: 400px;
			word-wrap: break-word;
			white-space: normal;
			flex-wrap: wrap;
		}

		&::after {
			content: '';
			position: absolute;
			top: 100%;
			left: 42px;
			margin-left: -8px;
			border-width: 8px;
			border-style: solid;
			border-color: #ffff transparent transparent transparent;
		}
		.tooltip-message {
			@media (max-width: 1200px) {
				display: none;
			}
		}
	}
`
